import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { realtimeClient } from "./engine";
import type { ClientRealtimeFeature } from "./types";

type ClientRealtimeSubscriberProps = {
  clientId?: string | null;
  features: ClientRealtimeFeature[];
};

/**
 * Keeps the client in view in sync with what the consumers commit: each event on `client:<clientId>`
 * invalidates the queries it made stale. A query on screen refetches at once; one that is not is marked
 * stale and refetches when its page renders it again.
 *
 * The hub does not replay frames sent while the socket was down, so a rejoin invalidates everything the
 * features own instead.
 */
export function ClientRealtimeSubscriber({ clientId, features }: ClientRealtimeSubscriberProps) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!clientId) return;

    let joined = false;
    let channel = realtimeClient.channel(`client:${clientId}`);

    for (const feature of features) {
      for (const [event, invalidate] of Object.entries(feature.events)) {
        channel = channel.on({ event }, (payload) => void invalidate(queryClient, payload));
      }
    }

    channel.subscribe((status) => {
      if (status !== "SUBSCRIBED") return;
      if (joined) {
        for (const feature of features) void queryClient.invalidateQueries({ queryKey: feature.resetKey });
      }
      joined = true;
    });

    return () => {
      void channel.remove();
    };
  }, [clientId, features, queryClient]);

  return null;
}